import { UseQueryResult } from '@tanstack/react-query'
import { add, addDays, format, startOfDay } from 'date-fns'
import { useMemo } from 'react'

import { useBookingContext } from '@providers/booking'
import type { TBookingContext, TCartGroom, TCartGroup } from '@providers/booking/types'
import {
  completeISODate,
  dateTimeToPeriod,
  dayToJSDate,
  ISOTimeToCurrentDate,
  toISODate,
} from 'components-ui/src/shared/dates'
import { hasRequestDates, hasRequestTime } from 'components-ui/src/shared/location'
import { Types } from 'service-api'
import { useClientBookingReservation } from 'service-api/src/hooks'

const DATE_TIME_FORMAT = 'yyyy-MM-dd'

interface TPetAddons {
  id: string
  qty: number
  petIndex: number
}

export const useBookingReservation = (
  locationName: string
): UseQueryResult<Types.BookingReservationClientDto | null> => {
  const bookingCtx = useBookingContext()

  const bookingOrder: Types.CreateBookingOrderClientInput | undefined = useMemo(() => {
    if (typeof bookingCtx.state.startDate === 'undefined') {
      return
    }
    const serviceTypeName = bookingCtx.state.data.serviceType?.serviceType?.name || 'boarding'
    const isGrooming = serviceTypeName === 'grooming'
    const isTemperTest = serviceTypeName === 'temper-test'

    return {
      serviceTypeName: serviceTypeName!,
      isSubStatusV2: true,
      pets:
        serviceTypeName === 'packages'
          ? [{ locationSpeciesId: bookingCtx.state.cart.groups[0].specieId || null }]
          : buildPets(bookingCtx.state.cart.groups),
      reservations: [
        ...(isGrooming || isTemperTest ? [] : buildReservation(bookingCtx.state, serviceTypeName!)),
        ...buildGroomingReservation(bookingCtx.state),
        ...(!isTemperTest ? [] : buildTemperTestReservations(bookingCtx.state)),
      ],
    } as Types.CreateBookingOrderClientInput
  }, [bookingCtx.state])
  return useClientBookingReservation(locationName, bookingOrder)
}

const buildPets = (groups: TCartGroup[]) =>
  groups
    .map((group) =>
      group.pets.map((p) => ({
        // if the pet.id length is less than 12, is a local generated id,
        // we don't need to send this one
        locationPetProfileId: p.id,
        displayName: p.name,
        locationSpeciesId: group.specieId,
        breedId: p.breedId,
        weight: p.weight ? parseFloat(p.weight) : undefined,
        birthdate: p.birthdate,
        sex: p.sex,
        altered: p.altered,
      }))
    )
    .flat()

const getDateRanges = (state: TBookingContext) => {
  if (!state.startDate) {
    return []
  }
  if (state.serviceTypeName === 'daycare') {
    return state.cart.groups
      .map((group) => {
        return group.daycareSelected!.map(({ date }) => ({
          startDate: format(date, DATE_TIME_FORMAT),
          endDate: format(add(date, { days: 1 }), DATE_TIME_FORMAT),
        }))
      })
      .flat()
  }
  return [
    {
      startDate: format(state.startDate!, DATE_TIME_FORMAT),
      endDate: format(state.endDate!, DATE_TIME_FORMAT),
    },
  ]
}

export const buildDaycareAddons = (startDate: string, group: TCartGroup, petIndexes: number[]) => {
  const daycareDate = startOfDay(Date.parse(completeISODate(startDate))).getTime()
  return (
    group.petsAddOnDaycare
      ?.map((addOn) => {
        return addOn.pets.reduce((acc, pet) => {
          pet.dates.forEach((date) => {
            if (date.dateSelected == daycareDate && date.qty > 0) {
              acc.push({
                id: addOn.offer.id!,
                qty: date.qty,
                petIndex: petIndexes?.[group.pets.findIndex((p) => p.id === pet.id)],
              })
            }
          })
          return acc
        }, [] as TPetAddons[]) as TPetAddons[]
      })
      ?.flat() || ([] as TPetAddons[])
  )
}

export const buildServiceAddons = (group: TCartGroup, petIndexes: number[]) => {
  const regularServices = group.roomsAddOn?.map((addOn) => ({ id: addOn.offer.id!, qty: 1 })) || []
  const requiredAddons =
    group.requiredAddOns?.map((addOn, idx) => ({
      id: addOn.offer.id!,
      qty: 1,
      required: true,
      petIndex: petIndexes[idx],
    })) || []

  return [...regularServices, ...requiredAddons]
}

export const buildReservation = (state: TBookingContext, serviceTypeName: string) => {
  const dateRanges = getDateRanges(state)
  return dateRanges
    .map((dr) => {
      let counter = 0
      return state.cart.groups.map((group) => {
        const groupPets = group.pets
        const petLength = groupPets.length
        const petIndexes = Array.from<number>({ length: petLength })
          .fill(0)
          .map((v, i) => v + i + counter)
        counter += petLength

        const petAddOns: TPetAddons[] =
          group.petsAddOn
            ?.map((addOn) =>
              addOn.pets.map((pet) => ({
                id: addOn.offer.id!,
                qty: pet.qty,
                petIndex: petIndexes?.[groupPets.findIndex((p) => p.id === pet.id)],
              }))
            )
            ?.flat() || []

        const petDaycareAddOns =
          serviceTypeName === 'daycare' ? buildDaycareAddons(dr.startDate, group, petIndexes) : []

        return {
          serviceTypeName,
          petIndexes: serviceTypeName === 'packages' ? [0] : petIndexes,
          startDate: dr.startDate,
          endDate: dr.endDate,
          primaryOffers: [
            {
              id: group.offerSelected!.offer.id!,
              qty: 1,
              serviceOffers: buildServiceAddons(group, petIndexes),
              petOffers: petAddOns.concat(petDaycareAddOns).flat(),
            },
          ],
        }
      })
    })
    .flat()
}
export const buildGroomingReservation = (state: TBookingContext) => {
  const dateRanges = getDateRanges(state)
  // for daycare, we'll use first day, and for boarding, we'll use last date of the date range.
  // Either way we only want the first dateRange object
  const dr = dateRanges[0]
  let counter = 0
  const isStandalone = state.serviceTypeName === 'grooming'
  const isPrimaryOvernight = !!state.data.serviceType?.serviceType?.overnight
  const isDateRequest = isStandalone
    ? hasRequestDates(state.config.serviceType)
    : hasRequestDates(state.data.inStayBookingConf)
  const isDateTimeRequest = isStandalone
    ? hasRequestTime(state.config.serviceType)
    : hasRequestTime(state.data.inStayBookingConf)

  // invoice for each petGroom inside grooms on each group
  return state.cart.groups
    .map((group) => {
      const petLength = group.pets.length
      const obj = group.grooms
        .map((petGrooms, petIdx) => {
          const primaryOffersPerPet: {
            id: string
            qty: number
            petOffers?: { id: string; qty: number; petIndex: number }[]
            overridePrimaryResourceId?: string
          }[] = []
          const addonIfApplicable = group.groomAddOn?.[petIdx] ? group.groomAddOn[petIdx] : []
          const petGroomAddons: { id: string; qty: number; petIndex: number; overridePrimaryResourceId?: string }[] = []
          const isAnyGroomer = isDateRequest && petGrooms.resource?.id == 'anyGroomer'

          addonIfApplicable.map((petAddon) => {
            petGroomAddons.push({
              id: petAddon.offer.id!,
              qty: 1,
              petIndex: counter + petIdx,
            })
          })
          petGrooms.offers.map((groom, i) => {
            primaryOffersPerPet.push({
              id: groom.offer.id!,
              qty: 1,
              petOffers: petGrooms.offers.length - 1 == i ? petGroomAddons : [],
            })
          })

          if (primaryOffersPerPet.length === 0) {
            return false
          }

          const payload = {
            serviceTypeName: 'grooming',
            // add up all pets so far to get current pet's index
            petIndexes: [counter + petIdx],
            primaryOffers: primaryOffersPerPet,
            ...(isAnyGroomer ? {} : { overridePrimaryResourceId: petGrooms.resource?.id }),
          }

          const periods = [] as GenericSimpleBag[]
          if (isStandalone) {
            if (isDateTimeRequest) {
              periods.push(buildDateTimeRequest(petGrooms))
            } else if (isDateRequest) {
              periods.push(buildDateRequest(petGrooms))
            } else {
              const tomorrow = addDays(new Date(), 1)
              periods.push({
                startDate: toISODate(tomorrow),
                endDate: toISODate(addDays(tomorrow, 1)),
              })
            }
            return {
              ...payload,
              requestPeriods: periods,
            }
          } else {
            if (isDateRequest) {
              periods.push(isDateTimeRequest ? buildDateTimeRequest(petGrooms) : buildDateRequest(petGrooms))
            } else {
              const period = isPrimaryOvernight
                ? { startDate: dr.endDate, endDate: toISODate(addDays(dayToJSDate(dr.endDate)!, 1)) }
                : { startDate: dr.startDate, endDate: dr.endDate }
              periods.push(period)
            }

            return {
              ...payload,
              requestPeriods: periods,
            }
          }
        })
        .filter((i) => i !== false)
        .flat()
      counter += petLength
      return obj
    })
    .flat()
}

// We can not filter group by temperTest because we need to keep the index to build
// the petIndex, we filter at the end by null
export const buildTemperTestReservations = (state: TBookingContext) => {
  let ctr = 0
  const petIndexes: number[] = state.cart.groups.reduce((acc, group) => {
    group.pets.forEach(() => {
      acc.push(ctr)
      ctr++
    })
    return acc
  }, [] as number[])

  if (petIndexes.length === 0) {
    return []
  }

  const temperTest = state.cart.temperTest!
  return [
    {
      serviceTypeName: state.data.temperTest?.serviceType?.name,
      petIndexes,
      startDate: format(temperTest.date!, DATE_TIME_FORMAT),
      endDate: format(add(temperTest.date!, { days: 1 }), DATE_TIME_FORMAT),
      startTime: temperTest.timez!,
      primaryOffers: [
        {
          id: state.data.temperTestOffer?.offer?.id || '',
          qty: 1,
          serviceOffers: [],
          petOffers: [],
        },
      ],
    },
  ]
}

const buildDateTimeRequest = (petGrooms: TCartGroom) => {
  const reqDate = ISOTimeToCurrentDate(
    petGrooms.requestDates?.[0]?.date,
    petGrooms.requestDates?.[0]?.arrivalTime?.slotStartTime || ''
  )
  const tmpPeriod = dateTimeToPeriod(reqDate, 0) || {}

  if ('endTime' in tmpPeriod) {
    delete tmpPeriod.endTime
  }
  return tmpPeriod
}

const buildDateRequest = (petGrooms: TCartGroom) => {
  return {
    startDate: format(petGrooms.requestDates?.[0]?.date, DATE_TIME_FORMAT),
    endDate: format(add(petGrooms.requestDates?.[0]?.date, { days: 1 }), DATE_TIME_FORMAT),
  }
}
